/**
 * MedicationFrequency Component
 *
 * Displays two charts for visualizing medication patterns:
 * 1. Scatter plot showing medication events by time of day and date
 * 2. Stacked bar chart showing daily frequency by medication
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import { format, parseISO, differenceInCalendarDays, subDays, startOfDay } from 'date-fns';
import { eventApi } from '../services/api';
import { getBabyKey, decryptEventData } from '../crypto';

interface MedicationFrequencyProps {
  babyId: string;
}

interface ScatterPoint {
  timeOfDay: number; // minutes since midnight (0-1439)
  dayIndex: number;
  date: string;
  time: string;
  medicationName: string;
  dose: string | null;
  colorIndex: number;
}

interface DailyBarData {
  date: string;
  dayIndex: number;
  [medication: string]: string | number; // medication name -> count
}

interface DataEvent {
  id: string;
  timestamp: string;
  encryptedData: string;
}

const PERIODS = [7, 14, 30] as const;
type Period = (typeof PERIODS)[number];

const COLOR_PALETTE = [
  '#06b6d4', // cyan
  '#a855f7', // purple
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#3b82f6', // blue
  '#ec4899', // pink
  '#f97316', // orange
];

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12AM';
  if (hour < 12) return `${hour}AM`;
  if (hour === 12) return '12PM';
  return `${hour - 12}PM`;
}

export default function MedicationFrequency({ babyId }: MedicationFrequencyProps) {
  const [period, setPeriod] = useState<Period>(7);

  const startDate = useMemo(() => {
    return startOfDay(subDays(new Date(), period)).toISOString();
  }, [period]);

  // Fetch MEDICATION events
  const { data: medEventsRaw, isLoading: medLoading } = useQuery({
    queryKey: ['medication-frequency', babyId, 'MEDICATION', period],
    queryFn: async (): Promise<DataEvent[]> => {
      const response = await eventApi.list(babyId, {
        type: 'MEDICATION',
        startDate,
        limit: '1000',
      });
      return (response.data?.events as DataEvent[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Decrypt and transform chart data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['medication-frequency-chart', babyId, period, medEventsRaw],
    queryFn: async () => {
      if (!medEventsRaw || medEventsRaw.length === 0) {
        return { scatterPoints: [], dailyData: [], medicationNames: [] };
      }

      const babyKey = await getBabyKey(babyId);
      if (!babyKey) return { scatterPoints: [], dailyData: [], medicationNames: [] };

      const periodStart = startOfDay(subDays(new Date(), period));
      const scatterPoints: ScatterPoint[] = [];
      const medicationSet = new Set<string>();
      // Map: dayIndex -> medication name -> count
      const dailyCounts = new Map<number, Map<string, number>>();

      for (const event of medEventsRaw) {
        try {
          const decrypted = await decryptEventData(event.encryptedData, babyKey);
          if (!decrypted || !decrypted.medicationName) continue;

          const timestamp = parseISO(event.timestamp);
          const hours = timestamp.getHours();
          const minutes = timestamp.getMinutes();
          const timeOfDay = hours * 60 + minutes;
          const dayIndex = differenceInCalendarDays(timestamp, periodStart);
          const medName = String(decrypted.medicationName);

          medicationSet.add(medName);

          scatterPoints.push({
            timeOfDay,
            dayIndex,
            date: format(timestamp, 'MMM d'),
            time: format(timestamp, 'h:mm a'),
            medicationName: medName,
            dose: decrypted.dose ? String(decrypted.dose) : null,
            colorIndex: 0, // will be set after we know all medication names
          });

          // Accumulate daily counts
          if (!dailyCounts.has(dayIndex)) {
            dailyCounts.set(dayIndex, new Map());
          }
          const dayMap = dailyCounts.get(dayIndex)!;
          dayMap.set(medName, (dayMap.get(medName) || 0) + 1);
        } catch {
          // Skip events that fail to decrypt
        }
      }

      const medicationNames = Array.from(medicationSet).sort();
      const medColorMap = new Map<string, number>();
      medicationNames.forEach((name, i) => {
        medColorMap.set(name, i % COLOR_PALETTE.length);
      });

      // Set colorIndex on scatter points
      for (const point of scatterPoints) {
        point.colorIndex = medColorMap.get(point.medicationName) ?? 0;
      }

      // Build daily bar data
      const dailyData: DailyBarData[] = [];
      for (let d = 0; d <= period; d++) {
        const date = subDays(new Date(), period - d);
        const entry: DailyBarData = {
          date: format(date, 'MMM d'),
          dayIndex: d,
        };
        const dayMap = dailyCounts.get(d);
        if (dayMap) {
          for (const [medName, count] of dayMap) {
            entry[medName] = count;
          }
        }
        // Only include days that have data or are within range
        dailyData.push(entry);
      }

      return { scatterPoints, dailyData, medicationNames };
    },
    enabled: !medLoading,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = medLoading || chartLoading;
  const scatterPoints = chartData?.scatterPoints ?? [];
  const dailyData = chartData?.dailyData ?? [];
  const medicationNames = chartData?.medicationNames ?? [];

  // Build scatter groups by medication
  const scatterGroups = useMemo(() => {
    const groups = new Map<string, ScatterPoint[]>();
    for (const point of scatterPoints) {
      if (!groups.has(point.medicationName)) {
        groups.set(point.medicationName, []);
      }
      groups.get(point.medicationName)!.push(point);
    }
    return groups;
  }, [scatterPoints]);

  // Dynamic scatter chart height
  const scatterHeight = Math.max(200, period * 18 + 60);

  // Custom scatter tooltip
  const ScatterTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ScatterPoint;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">{data.medicationName}</p>
          {data.dose && (
            <p className="text-sm text-gray-600 dark:text-gray-300">Dose: {data.dose}</p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-300">{data.date} at {data.time}</p>
        </div>
      );
    }
    return null;
  };

  // Custom bar tooltip
  const BarTooltipContent = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const totalForDay = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
          {payload.map((entry: any, index: number) => (
            entry.value > 0 && (
              <p key={index} className="text-sm" style={{ color: entry.fill }}>
                {entry.name}: {entry.value}
              </p>
            )
          ))}
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Total: {totalForDay} dose{totalForDay !== 1 ? 's' : ''}
          </p>
        </div>
      );
    }
    return null;
  };

  // X-axis tick formatter for scatter (time of day)
  const timeTickFormatter = (value: number) => {
    const h = Math.floor(value / 60);
    return formatHourLabel(h);
  };

  // Y-axis tick formatter for scatter (date)
  const dateTickFormatter = (value: number) => {
    const date = subDays(new Date(), period - value);
    return format(date, 'MMM d');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
      {/* Header with period selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Medication Frequency
        </h3>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading medication data...</p>
        </div>
      ) : scatterPoints.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No medication data for the last {period} days.
          </p>
        </div>
      ) : (
        <>
          {/* Chart 1: Medication Times Scatter */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Medication Times
            </h4>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-2 text-xs text-gray-500 dark:text-gray-400">
              {medicationNames.map((name, i) => (
                <span key={name} className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: COLOR_PALETTE[i % COLOR_PALETTE.length] }}
                  ></span>
                  {name}
                </span>
              ))}
            </div>

            <div style={{ height: scatterHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    type="number"
                    dataKey="timeOfDay"
                    domain={[0, 1440]}
                    ticks={[0, 180, 360, 540, 720, 900, 1080, 1260]}
                    tickFormatter={timeTickFormatter}
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                  />
                  <YAxis
                    type="number"
                    dataKey="dayIndex"
                    domain={[0, period]}
                    reversed
                    tickFormatter={dateTickFormatter}
                    tickCount={Math.min(period, 8)}
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                    width={45}
                  />
                  <Tooltip content={<ScatterTooltipContent />} />

                  {Array.from(scatterGroups.entries()).map(([medName, points]) => {
                    const colorIdx = points[0]?.colorIndex ?? 0;
                    const color = COLOR_PALETTE[colorIdx];
                    return (
                      <Scatter key={medName} name={medName} data={points}>
                        {points.map((_, index) => (
                          <Cell key={`${medName}-${index}`} fill={color} r={6} />
                        ))}
                      </Scatter>
                    );
                  })}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Daily Frequency Stacked Bar */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Daily Frequency
            </h4>

            <div style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="date"
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                    interval={period <= 7 ? 0 : period <= 14 ? 1 : 3}
                  />
                  <YAxis
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                    width={30}
                    allowDecimals={false}
                  />
                  <Tooltip content={<BarTooltipContent />} />
                  <Legend />
                  {medicationNames.map((medName, i) => (
                    <Bar
                      key={medName}
                      dataKey={medName}
                      stackId="a"
                      fill={COLOR_PALETTE[i % COLOR_PALETTE.length]}
                      radius={i === medicationNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
